import { z } from 'zod';

import {
  PRODUCT_ANALYTICS_EXCLUSION_BATCH_MAX,
  PRODUCT_ANALYTICS_METRICS,
} from '../productAnalytics.js';
import { sgtDateKey } from '../sgtDay.js';

export const AnalyticsObjectIdSchema = z
  .string()
  .regex(/^[a-f\d]{24}$/i)
  .transform(id => id.toLowerCase());
export const AnalyticsDaySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(day => {
    const parsed = new Date(`${day}T00:00:00Z`);
    return (
      Number.isFinite(parsed.getTime()) &&
      parsed.toISOString().slice(0, 10) === day
    );
  }, 'Enter a real calendar date');
export const AnalyticsPopulationSchema = z.enum([
  'regular',
  'nonprofit',
  'all',
  'compare',
]);
export const AnalyticsMetricIdSchema = z.enum(
  Object.keys(PRODUCT_ANALYTICS_METRICS) as [
    keyof typeof PRODUCT_ANALYTICS_METRICS,
    ...(keyof typeof PRODUCT_ANALYTICS_METRICS)[],
  ]
);
export const AnalyticsDateFields = {
  preset: z.enum(['7d', '28d', '90d', 'today', 'custom']).default('28d'),
  from: AnalyticsDaySchema.optional(),
  to: AnalyticsDaySchema.optional(),
  asOf: z.string().datetime({ offset: true }).optional(),
};
export const AnalyticsReportFields = {
  ...AnalyticsDateFields,
  population: AnalyticsPopulationSchema.default('regular'),
  k: z.coerce.number().int().min(1).max(100).default(5),
  organizationId: AnalyticsObjectIdSchema.optional(),
  cohortId: AnalyticsObjectIdSchema.optional(),
};
export const AnalyticsPaginationFields = {
  page: z.coerce.number().int().min(1).max(1_000_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
};
export const AnalyticsRetentionFields = {
  kind: z.enum(['student', 'teacher']).default('student'),
  cohortStart: AnalyticsDaySchema.optional(),
  week: z.coerce.number().int().min(1).max(4).optional(),
};

/** Dates are inclusive Singapore UI dates; server periods owns UTC intervals. */
export function validateAnalyticsDates(
  value: { preset: string; from?: string; to?: string; asOf?: string },
  ctx: z.RefinementCtx,
  now: Date = new Date()
): void {
  const issue = (path: string, message: string) =>
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
  if (value.preset === 'custom' && (!value.from || !value.to))
    issue('from', 'Custom dates require From and To');
  if (Boolean(value.from) !== Boolean(value.to))
    issue('to', 'Provide both From and To');
  if (value.from && value.to) {
    if (value.from > value.to) issue('to', 'To must be on or after From');
    const anniversary = new Date(`${value.from}T00:00:00Z`);
    if (Number.isFinite(anniversary.getTime())) {
      anniversary.setUTCFullYear(anniversary.getUTCFullYear() + 1);
      if (new Date(`${value.to}T00:00:00Z`).getTime() >= anniversary.getTime())
        issue('to', 'Select at most one calendar year');
    }
    if (value.to > sgtDateKey(now))
      issue('to', 'Future reporting dates are unavailable');
  }
  if (value.asOf && new Date(value.asOf).getTime() > now.getTime())
    issue('asOf', 'Observation time cannot be in the future');
}

export const ProductAnalyticsQuerySchema = z
  .object(AnalyticsReportFields)
  .strict()
  .superRefine(validateAnalyticsDates);
export type ProductAnalyticsQuery = z.infer<typeof ProductAnalyticsQuerySchema>;
export const ProductAnalyticsDemoQuerySchema = z
  .object(AnalyticsDateFields)
  .strict()
  .superRefine(validateAnalyticsDates);
export type ProductAnalyticsDemoQuery = z.infer<
  typeof ProductAnalyticsDemoQuerySchema
>;
export const ProductAnalyticsRetentionQuerySchema = z
  .object({ ...AnalyticsReportFields, ...AnalyticsRetentionFields })
  .strict()
  .superRefine(validateAnalyticsDates);
export type ProductAnalyticsRetentionQuery = z.infer<
  typeof ProductAnalyticsRetentionQuerySchema
>;

export const ProductAnalyticsMetricUsersQuerySchema = z
  .object({
    ...AnalyticsReportFields,
    ...AnalyticsPaginationFields,
    ...AnalyticsRetentionFields,
    view: z.literal('metric').default('metric'),
    metric: AnalyticsMetricIdSchema,
    sort: z.enum(['name', 'count', 'first', 'last']).default('name'),
  })
  .strict()
  .superRefine((value, ctx) => {
    validateAnalyticsDates(value, ctx);
    if (
      (value.metric === 'original_cohort' ||
        value.metric === 'returning_cohort') &&
      !value.cohortStart
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cohortStart'],
        message: 'Choose a cohort',
      });
    }
    if (value.metric === 'returning_cohort' && !value.week) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['week'],
        message: 'Choose a return week',
      });
    }
  });
export type ProductAnalyticsMetricUsersQuery = z.infer<
  typeof ProductAnalyticsMetricUsersQuerySchema
>;
export const ProductAnalyticsCandidatesQuerySchema = z
  .object({
    ...AnalyticsPaginationFields,
    view: z.literal('exclusion-candidates'),
    search: z.string().trim().max(100).default(''),
  })
  .strict();
export type ProductAnalyticsCandidatesQuery = z.infer<
  typeof ProductAnalyticsCandidatesQuerySchema
>;
/** Tutors a nudge can name: teaching accounts in the tenant, any report filter. */
export const ProductAnalyticsNudgeCandidatesQuerySchema = z
  .object({
    ...AnalyticsPaginationFields,
    view: z.literal('nudge-candidates'),
    search: z.string().trim().max(100).default(''),
  })
  .strict();
export type ProductAnalyticsNudgeCandidatesQuery = z.infer<
  typeof ProductAnalyticsNudgeCandidatesQuerySchema
>;
export const ProductAnalyticsUsersQuerySchema = z.union([
  ProductAnalyticsCandidatesQuerySchema,
  ProductAnalyticsNudgeCandidatesQuerySchema,
  ProductAnalyticsMetricUsersQuerySchema,
]);
export type ProductAnalyticsUsersQuery = z.infer<
  typeof ProductAnalyticsUsersQuerySchema
>;
export const ProductAnalyticsUptakeQuerySchema = z
  .object({
    ...AnalyticsReportFields,
    ...AnalyticsPaginationFields,
    view: z.enum(['pairs', 'students']).default('pairs'),
    uptake: z.enum(['answered', 'submitted']).default('answered'),
    observation: z.enum(['all', 'complete', 'provisional']).default('all'),
  })
  .strict()
  .superRefine(validateAnalyticsDates);
export type ProductAnalyticsUptakeQuery = z.infer<
  typeof ProductAnalyticsUptakeQuerySchema
>;
/** The audited decision one command applies to every account it names. */
export const ProductAnalyticsExclusionDecisionSchema = z
  .object({
    excluded: z.boolean(),
    reason: z.string().trim().min(1).max(300),
  })
  .strict();
export type ProductAnalyticsExclusionDecision = z.infer<
  typeof ProductAnalyticsExclusionDecisionSchema
>;
export const ProductAnalyticsExclusionCommandSchema =
  ProductAnalyticsExclusionDecisionSchema.extend({
    userIds: z
      .array(AnalyticsObjectIdSchema)
      .min(1)
      .max(PRODUCT_ANALYTICS_EXCLUSION_BATCH_MAX),
  }).strict();
export type ProductAnalyticsExclusionCommand = z.infer<
  typeof ProductAnalyticsExclusionCommandSchema
>;
/**
 * An administrator reminded a tutor outside the product. Ids only: what was
 * said never enters the TeacherEvent stream.
 */
export const ProductAnalyticsNudgeCommandSchema = z
  .object({
    teacherUserId: AnalyticsObjectIdSchema,
    classCodeId: AnalyticsObjectIdSchema.optional(),
  })
  .strict();
export type ProductAnalyticsNudgeCommand = z.infer<
  typeof ProductAnalyticsNudgeCommandSchema
>;
export const ProductAnalyticsExclusionsQuerySchema = z.union([
  z
    .object({
      ...AnalyticsPaginationFields,
      view: z.literal('current').default('current'),
    })
    .strict(),
  z
    .object({
      ...AnalyticsPaginationFields,
      view: z.literal('history'),
      userId: AnalyticsObjectIdSchema,
    })
    .strict(),
]);
export type ProductAnalyticsExclusionsQuery = z.infer<
  typeof ProductAnalyticsExclusionsQuerySchema
>;

/** URL vocabulary. A pure client parser salvages invalid fields with a notice. */
export const AnalyticsPageFields = {
  ...AnalyticsReportFields,
  ...AnalyticsPaginationFields,
  ...AnalyticsRetentionFields,
  tab: z.enum(['overview', 'students', 'teachers', 'demo']).default('overview'),
  detail: z.enum(['users', 'uptake', 'exclusions', 'history']).optional(),
  metric: AnalyticsMetricIdSchema.optional(),
  sort: z.enum(['name', 'count', 'first', 'last']).default('name'),
  chart: z.enum(['chart', 'table']).default('chart'),
  trend: z
    .enum(['meaningful_practice', 'dau', 'practice_habit'])
    .default('meaningful_practice'),
  uptake: z.enum(['answered', 'submitted']).default('answered'),
  uptakeView: z.enum(['pairs', 'students']).default('pairs'),
  observation: z.enum(['all', 'complete', 'provisional']).default('all'),
  userId: AnalyticsObjectIdSchema.optional(),
};
export const ProductAnalyticsPageQuerySchema = z
  .object(AnalyticsPageFields)
  .strict()
  .superRefine(validateAnalyticsDates);
export type ProductAnalyticsPageQuery = z.infer<
  typeof ProductAnalyticsPageQuerySchema
>;
